import { useRef } from 'react';
import { useDynamicList, useSubmit, SubmitResult } from '@shopify/react-form';
import gettext from '@sdk/lang/gettext';
import useSlingApi from '@sdk/api/useSlingApi';
import { GENERIC_SERVER_ERROR } from '@sdk/login/constants';

import type { UpdateWages } from '@web/onboarding/Checklist/onboardingChecklist.types';
import type { Employee } from '@web/onboarding/Checklist/DefineWagesModal/defineWagesModal.types';
import { getSubmitError } from '@web/forms/utils/getSubmitError';
import { FormSubmitStatuses } from '@web/forms/types';
import {
  emptyEmployeeFactory,
  getWagesPayloadFromEmployeesFields,
  getWageError,
} from '@web/onboarding/Checklist/DefineWagesModal/defineWagesModal.utils';
import { decorateSubmit } from '@web/forms/utils/decorateSubmit';

const useDefineWagesForEmployeesForm = (employees: Employee[], updateWages: UpdateWages) => {
  const api = useSlingApi();
  const { fields, dirty } = useDynamicList(
    {
      list: employees,
      validates: {
        wage: [getWageError],
      },
    },
    emptyEmployeeFactory
  );

  const submitPromise = useRef<Promise<SubmitResult>>();
  const {
    submit: formSubmit,
    errors,
    setErrors,
  } = useSubmit(
    async ({ employees }) => {
      const hasSpecifiedAtLeastOneWage = employees.some(({ wage }) => Boolean(Number(wage)));

      if (!hasSpecifiedAtLeastOneWage) {
        submitPromise.current = getSubmitError(gettext('Please specify at least one basic wage'));
        return submitPromise.current;
      }

      const wagesToSave = getWagesPayloadFromEmployeesFields(employees);
      const wagesUpdatePromises = wagesToSave.map((userWage) =>
        api.laborCost.updateBaseWages([userWage])
      );

      try {
        const results = await Promise.all(wagesUpdatePromises);
        results.forEach(({ data }) => {
          updateWages({ wages: data });
        });
      } catch (e) {
        submitPromise.current = getSubmitError(e.data?.message || GENERIC_SERVER_ERROR);
        return submitPromise.current;
      }

      submitPromise.current = Promise.resolve({ status: FormSubmitStatuses.success });
      return submitPromise.current;
    },
    { employees: fields }
  );

  const submit = decorateSubmit(formSubmit, submitPromise);

  return {
    fields,
    dirty,
    submit,
    errors,
    setErrors,
  };
};

export default useDefineWagesForEmployeesForm;
